import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { DropdownOption, GlobalDropdownField } from '../../../components/GlobalComponents/GlobalDropdownField';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';
import { GlobalCheckboxes, CheckboxOption } from '../../../components/GlobalComponents/GlobalCheckboxes';
import useClassesStore from '../../../stateManagers/store/useClassesStore';
import { useExamTypes } from '../../../stateManagers/hooks/useExamTypes';
import { useSubjectsByClass } from '../../../stateManagers/hooks/useSubjectsByClass';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { modalStyles } from './subparts/modalStyles';
import { Nullable } from 'primereact/ts-helpers';

interface ExamEntry {
  id: string;
  subjectId: string;
  examDate: string;
}

interface AddExamDateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentSession: string;
}

const AddExamDateModal: React.FC<AddExamDateModalProps> = ({
  open,
  onClose,
  onSuccess,
  currentSession,
}) => {
  const toast = useRef<Toast>(null);
  const [formData, setFormData] = useState<{
    classId: string;
    examTypeId: string;
    sections: string[];
    entries: ExamEntry[];
  }>({
    classId: '',
    examTypeId: '',
    sections: [],
    entries: [{
      id: crypto.randomUUID(),
      subjectId: '',
      examDate: new Date().toISOString().split('T')[0],
    }],
  });

  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const { classOptions, fetchClasses, isFetching } = useClassesStore();
  const { examTypes, loading: examTypesLoading } = useExamTypes();

  const selectedClass = classOptions.find((c) => c.id === formData.classId);
  const selectedClassSections = selectedClass?.sections ?? [];
  const hasSections = selectedClassSections.length > 0;
  const allSectionsSelected = hasSections && formData.sections.length === selectedClassSections.length;

  const {
    subjectOptions,
    loading: subjectsLoading
  } = useSubjectsByClass(formData.classId || null, true);

  useEffect(() => {
    if (open) {
      fetchClasses(true);
      setFormData({
        classId: '',
        examTypeId: '',
        sections: [],
        entries: [{
          id: crypto.randomUUID(),
          subjectId: '',
          examDate: new Date().toISOString().split('T')[0],
        }],
      });
    }
  }, [open, fetchClasses]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleAddEntry = () => {
    setFormData((prev) => ({
      ...prev,
      entries: [...prev.entries, {
        id: crypto.randomUUID(),
        subjectId: '',
        examDate: new Date().toISOString().split('T')[0],
      }],
    }));
  };

  const handleRemoveEntry = (id: string) => {
    if (formData.entries.length === 1) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'At least one entry is required',
        life: 3000,
      });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((e) => e.id !== id),
    }));
  };

  const handleUpdateEntry = (id: string, field: 'subjectId' | 'examDate', value: string) => {
    if (field === 'subjectId' && value) {
      const isDuplicate = formData.entries.some((e) => e.id !== id && e.subjectId === value);
      if (isDuplicate) {
        const subject = subjectOptions.find(s => s.value === value);
        toast.current?.show({
          severity: 'error',
          summary: 'Duplicate Subject',
          detail: `Subject "${subject?.label || value}" is already selected in another entry`,
          life: 4000,
        });
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((e) => e.id === id ? { ...e, [field]: value } : e),
    }));
  };

  const classDropdownOptions: DropdownOption[] = [
    { value: '', label: 'Select Class' },
    ...classOptions.map((c) => ({ value: c.id, label: c.label })),
  ];

  const examTypeOptions: DropdownOption[] = examTypesLoading
    ? [{ value: '', label: 'Loading exam types...' }]
    : [
        { value: '', label: 'Select Exam Type' },
        ...examTypes.map((et) => ({ value: et.value, label: et.label })),
      ];

  const subjectDropdownOptions: DropdownOption[] = !formData.classId
    ? [{ value: '', label: 'Select Class First' }]
    : subjectsLoading
      ? [{ value: '', label: 'Loading subjects...' }]
      : subjectOptions.length === 0
        ? [{ value: '', label: 'No subjects available' }]
        : subjectOptions;

  const sectionCheckboxOptions: CheckboxOption[] = [
    { value: '__SELECT_ALL__', label: 'Select All' },
    ...selectedClassSections.map((section) => ({
      value: section,
      label: `Section ${section}`,
    })),
  ];

  const handleSectionChange = (checked: string[]) => {
    const selectAllChecked = checked.includes('__SELECT_ALL__');
    const previousSelectAll = allSectionsSelected;

    if (selectAllChecked && !previousSelectAll) {
      setFormData(prev => ({ ...prev, sections: selectedClassSections }));
    } else if (!selectAllChecked && previousSelectAll) {
      setFormData(prev => ({ ...prev, sections: [] }));
    } else {
      const realSections = checked.filter(v => v !== '__SELECT_ALL__');
      setFormData(prev => ({ ...prev, sections: realSections }));
    }
  };

  const currentSectionValues = allSectionsSelected
    ? ['__SELECT_ALL__', ...selectedClassSections]
    : formData.sections;

  const handleSubmit = async () => {
    if (!formData.examTypeId) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please select an exam type', life: 5000 });
      return;
    }
    if (!formData.classId) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please select a class', life: 5000 });
      return;
    }
    if (hasSections && formData.sections.length === 0) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please select at least one section', life: 5000 });
      return;
    }

    const invalidEntries = formData.entries.filter(e => !e.subjectId || !e.examDate);
    if (invalidEntries.length > 0) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please complete all fields in every entry', life: 5000 });
      return;
    }

    for (const entry of formData.entries) {
      if (new Date(entry.examDate + 'T12:00:00') < new Date(new Date().setHours(0, 0, 0, 0))) {
        toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Exam dates cannot be in the past', life: 5000 });
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        entries: formData.entries.map((entry) => ({
          class_id: parseInt(formData.classId),
          section: formData.sections,
          subject_id: parseInt(entry.subjectId),
          exam_date: entry.examDate,
          session: currentSession,
          status: 'scheduled',
          exam_type_id: parseInt(formData.examTypeId),
        })),
      };

      await apiClient.post('/bulk-add-exam-date-sheets', payload);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `${formData.entries.length} exam ${formData.entries.length === 1 ? 'date' : 'dates'} added successfully!`,
        life: 4000,
      });

      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 300);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to add exam dates',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div
        style={{
          width: '54px',
          height: '54px',
          borderRadius: '14px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '24px',
          fontWeight: '700',
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
          flexShrink: 0,
          border: '3px solid white',
        }}
      >
        <i className="pi pi-file-edit" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af', marginBottom: '4px' }}>
          Add Exam Dates
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
          Schedule exam dates for multiple subjects
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.exam-date-modal .p-dialog');
      const mask = document.querySelector('.exam-date-modal');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '820px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="exam-date-modal"
      >
        <style>{modalStyles}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="field-section">
            <div className="section-header">
              <div className="icon-circle">
                <i className="pi pi-cog" />
              </div>
              <span>Global Settings</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <GlobalDropdownField
                label="Exam Type"
                value={formData.examTypeId}
                onChange={(v) => setFormData({ ...formData, examTypeId: v })}
                options={examTypeOptions}
                required
                disabled={loading}
                icon="pi pi-tag"
              />

              <GlobalDropdownField
                label="Class"
                value={formData.classId}
                onChange={(v) => setFormData({
                  ...formData,
                  classId: v,
                  sections: [],
                  entries: formData.entries.map(e => ({ ...e, subjectId: '' }))
                })}
                options={classDropdownOptions}
                required
                disabled={isFetching || loading}
                icon="pi pi-users"
              />

              {hasSections && (
                <GlobalCheckboxes
                  label="Sections"
                  options={sectionCheckboxOptions}
                  value={currentSectionValues}
                  onChange={handleSectionChange}
                  required
                  layout="horizontal"
                  gap={20}
                />
              )}

              {formData.classId && !hasSections && (
                <div className="info-note">
                  <i className="pi pi-info-circle" />
                  This class has no sections — exam will apply to the whole class
                </div>
              )}
            </div>
          </div>

          <div className="field-section">
            <div className="section-header">
              <div className="icon-circle">
                <i className="pi pi-list" />
              </div>
              <span>Exam Entries ({formData.entries.length})</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {formData.entries.map((entry, index) => (
                <div key={entry.id} className="entry-card">
                  <div className="entry-number">Entry {index + 1}</div>
                  {formData.entries.length > 1 && (
                    <button
                      className="remove-button"
                      onClick={() => handleRemoveEntry(entry.id)}
                      disabled={loading}
                    >
                      <i className="pi pi-times" style={{ fontSize: '11px' }} />
                    </button>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '8px' }}>
                    <GlobalDropdownField
                      label="Subject"
                      value={entry.subjectId}
                      onChange={(v) => handleUpdateEntry(entry.id, 'subjectId', v)}
                      options={subjectDropdownOptions}
                      required
                      disabled={!formData.classId || subjectsLoading || loading}
                      icon="pi pi-book"
                    />

                    <GlobalDateField
                      label="Exam Date"
                      value={entry.examDate ? new Date(entry.examDate + 'T12:00:00') : null}
                      onChange={(value: Nullable<Date | Date[] | (Date | null)[]>) => {
                        if (value instanceof Date) {
                          const year = value.getFullYear();
                          const month = String(value.getMonth() + 1).padStart(2, '0');
                          const day = String(value.getDate()).padStart(2, '0');
                          const isoDate = `${year}-${month}-${day}`;
                          handleUpdateEntry(entry.id, 'examDate', isoDate);
                        } else if (value === null) {
                          handleUpdateEntry(entry.id, 'examDate', '');
                        }
                      }}
                      required
                      disabled={loading}
                      dateFormat="dd/mm/yy"
                      minDate={new Date()}
                      showButtonBar={false}
                      monthNavigator
                      yearNavigator
                      yearRange="2010:2030"
                    />
                  </div>
                </div>
              ))}

              <button
                className="add-entry-button"
                onClick={handleAddEntry}
                disabled={loading || subjectsLoading}
              >
                <i className="pi pi-plus-circle" />
                Add Another Entry
              </button>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <GlobalButton
              label={loading ? 'Adding Entries...' : `Add ${formData.entries.length} ${formData.entries.length === 1 ? 'Entry' : 'Entries'}`}
              onClick={handleSubmit}
              variant="primary"
              size="medium"
              disabled={loading || isFetching || subjectsLoading || examTypesLoading}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddExamDateModal;